/* eslint-disable react-hooks/rules-of-hooks */
import classnames from "classnames";
import { isLeft } from "fp-ts/lib/Either";
import * as io from "io-ts";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DOMPurify from "isomorphic-dompurify";

import type { FeedResultFragment } from "@ihr-radioedit/inferno-webapi";
import { slugify } from "@ihr-radioedit/inferno-core";
import { getPaths } from "@ihr-radioedit/inferno-core";
import { ILog } from "@ihr-radioedit/inferno-core";
import { type Store, cookies, type AppEnvironment } from "@inferno/renderer-shared-core";
import { Button, MagicLink } from "../../ui";
import { ButtonKind } from "../../ui/Button.component";
import "./LegalNotice.style.scss";
import { inject } from "mobx-react";

const log = ILog.logger("LegalNotice.component");

interface LegalNoticeProps {
  data: FeedResultFragment[];
  store?: Store;
}

const storageKey = "LegalNotice";

const getStoredMessages = () => {
  let messages = [];
  if (cookies()) {
    const storedMessage = cookies().get(storageKey);
    if (storedMessage) {
      messages = JSON.parse(decodeURIComponent(storedMessage));
    }
  }
  return messages;
};

const storeMessages = (messages: string[], env: AppEnvironment, days = 365) => {
  const currentDomain =
    env.ENVIRONMENT === "production" && env.NODE_ENV === "production"
      ? ".iheart.com"
      : env.ENVIRONMENT === "stage" && env.NODE_ENV === "production"
      ? ".ihrint.com"
      : "localhost";

  const date = new Date();
  date.setDate(date.getDate() + days);

  cookies().setRaw(
    storageKey,
    JSON.stringify(messages),
    `expires=${date.toUTCString()}; domain=${currentDomain}; path=/; Secure; SameSite=Lax`,
  );
};

const acknowledge = (messageKey: string, env: AppEnvironment) => {
  const stored = getStoredMessages();
  if (!stored.includes(messageKey)) {
    stored.push(messageKey);
    storeMessages(stored, env);
  }
};

const messageSeen = (messageKey: string) => {
  const stored = getStoredMessages();
  return stored.includes(messageKey);
};

const LegalDataResolver = io.type({
  id: io.union([io.string, io.null]),
  record: io.type({
    title: io.union([io.string, io.null]),
    subtitle: io.union([io.string, io.null]),
    link: io.partial({
      name: io.union([io.string, io.null]),
      description: io.union([io.string, io.null]),
      target: io.union([io.string, io.null]),
      urls: io.union([
        io.type({
          web: io.union([io.string, io.null]),
        }),
        io.null,
      ]),
    }),
  }),
});

export const LegalNotice = inject("store")((props: LegalNoticeProps) => {
  const { store } = props;

  if (!store) {
    return null;
  }

  // Ensure that all of the data we need is present
  const [data] = props.data;
  const resolvedData = LegalDataResolver.decode(data);
  if (isLeft(resolvedData)) {
    log.error("Invalid data on legal block: ", getPaths(resolvedData));
    return null;
  }

  const {
    id,
    record: { title, link, subtitle: message },
  } = resolvedData.right;
  if (!title || !id || !message) {
    return null;
  }
  let url;
  let linkText;
  let linkTarget;
  if (link && link.urls && link.urls.web) {
    url = link.urls.web;
    linkText = link.name || "click here";
    linkTarget = link.target || "_blank";
  }

  // See if user has seen this message before
  const { t } = useTranslation();
  const [acknowledged, setAcknowledged] = useState(false);
  const messageKey = slugify(id);
  const userSawMessage = messageSeen(messageKey);
  let showMessage = false;
  if (!userSawMessage && !showMessage && !acknowledged) {
    showMessage = true;
  }

  // Auto-acknowledge
  useEffect(() => {
    acknowledge(messageKey, store.env);
  });

  // Render out component
  const messageClass = classnames("component-legal-notice", {
    show: showMessage,
  });
  return (
    <div className={messageClass}>
      <section className="message">
        <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message) }} />
        <MagicLink to={`${url}`} target={linkTarget}>
          {linkText}
        </MagicLink>
      </section>
      <Button
        className="acknowledgement"
        label="Acknowledge legal messaging"
        kind={ButtonKind.PRIMARY}
        click={() => setAcknowledged(true)}
      >
        {link?.description || t("dismiss")}
      </Button>
    </div>
  );
});

export default LegalNotice;
